// Pure model behind the lead page's 5-step checklist (LeadFlowStepper).
// Canonical closing flow: proposta criada → proposta assinada → contrato
// assinado → pagamento recebido → entregue. Each step is computed on its own
// from the documents, and "current" is simply the first step not done, so the
// operator always sees exactly one next action.

export type FlowStepKey = "proposal" | "proposal_signed" | "contract_signed" | "paid" | "delivered";
export type FlowStepState = "done" | "current" | "todo";

export type FlowAction =
  | "go-proposta"
  | "send-proposal"
  | "go-contrato"
  | "send-contract"
  | "go-payments"
  | "go-entrega"
  | "mark-delivered";

export interface FlowStep {
  key: FlowStepKey;
  label: string;
  state: FlowStepState;
  /** One-line status shown under the current step. */
  hint?: string;
  /** The single primary action for this step (only meaningful when current). */
  cta?: { label: string; action: FlowAction };
}

export interface FlowInput {
  leadStatus: string;
  budgets: Array<{ id: string; status: string; created_at: string }>;
  contracts: Array<{ id: string; status: string; budget_id: string | null }>;
  /** Installments of the budget that will be charged (see pickPaymentsBudget). */
  installments: Array<{ status: string }>;
  hasPaymentPlan: boolean;
  /**
   * The budget being charged is a 100% permuta (net_total fully settled in
   * services — see barterSplit). Nothing to bill, so the payment step is done
   * unless a legacy installment is still open.
   */
  barterOnly?: boolean;
}

export interface LeadFlow {
  steps: FlowStep[];
  /** Index of the current step, or -1 when everything is done. */
  currentIndex: number;
  current: FlowStep | null;
  lost: boolean;
}

const SIGNED = new Set(["signed", "accepted"]);
const IN_FLIGHT = new Set(["sent", "partially_signed"]);

/**
 * The budget whose payment plan the lead page shows. Prefers the most recent
 * signed/accepted budget; falls back to the budget linked to a signed
 * contract (legacy rows finalized before finalize-contract marked budgets
 * accepted). Returns null when nothing is closed yet.
 */
export function pickPaymentsBudget<B extends { id: string; status: string; created_at: string }>(
  budgets: B[],
  contracts: Array<{ status: string; budget_id: string | null }>,
): B | null {
  const signed = [...budgets]
    .filter((b) => SIGNED.has(b.status))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  if (signed[0]) return signed[0];
  const signedContract = contracts.find((c) => c.status === "signed" && c.budget_id);
  if (signedContract) return budgets.find((b) => b.id === signedContract.budget_id) ?? null;
  return null;
}

export function computeLeadFlow(input: FlowInput): LeadFlow {
  const {
    leadStatus,
    budgets,
    contracts,
    installments,
    hasPaymentPlan,
    barterOnly = false,
  } = input;
  const lost = leadStatus === "lost";

  const hasBudget = budgets.length > 0;
  const budgetSigned = budgets.some((b) => SIGNED.has(b.status));
  const budgetInFlight = budgets.some((b) => IN_FLIGHT.has(b.status));
  const budgetRejected =
    !budgetSigned && !budgetInFlight && budgets.some((b) => b.status === "rejected");

  const contractSigned = contracts.some((c) => c.status === "signed");
  const contractInFlight = contracts.some((c) => IN_FLIGHT.has(c.status));
  const contractRejected =
    !contractSigned && !contractInFlight && contracts.some((c) => c.status === "rejected");
  const hasContractDraft = contracts.some((c) => c.status === "draft");

  const paidCount = installments.filter((i) => i.status === "paid").length;
  const allPaid = installments.length > 0 && paidCount === installments.length;
  // A full permuta has no installments (vacuously paid); a leftover one still counts.
  const paidDone = barterOnly ? installments.every((i) => i.status === "paid") : allPaid;
  const delivered = leadStatus === "delivered";

  const steps: FlowStep[] = [
    {
      key: "proposal",
      label: "Proposta criada",
      state: hasBudget ? "done" : "todo",
      hint: "Defina escopo, valor e prazo na aba Proposta.",
      cta: { label: "Criar proposta", action: "go-proposta" },
    },
    {
      key: "proposal_signed",
      label: "Proposta assinada",
      state: budgetSigned ? "done" : "todo",
      hint: budgetInFlight
        ? "Aguardando o cliente assinar a proposta."
        : budgetRejected
          ? "O cliente recusou a proposta. Ajuste e reenvie."
          : "Envie a proposta para o cliente assinar.",
      cta: budgetInFlight
        ? { label: "Ver assinaturas", action: "go-proposta" }
        : budgetRejected
          ? { label: "Reenviar proposta", action: "send-proposal" }
          : { label: "Enviar proposta", action: "send-proposal" },
    },
    {
      key: "contract_signed",
      label: "Contrato assinado",
      state: contractSigned ? "done" : "todo",
      hint: contractInFlight
        ? "Aguardando o cliente assinar o contrato."
        : contractRejected
          ? "O cliente recusou o contrato. Ajuste e reenvie."
          : hasContractDraft
            ? "Contrato em rascunho — finalize e envie para assinatura."
            : "Crie o contrato e envie para assinatura.",
      cta: contractInFlight
        ? { label: "Ver assinaturas", action: "go-contrato" }
        : {
            label: hasContractDraft || contractRejected ? "Enviar contrato" : "Criar contrato",
            action: "go-contrato",
          },
    },
    {
      key: "paid",
      label: "Pagamento recebido",
      state: paidDone ? "done" : "todo",
      hint:
        barterOnly && paidDone
          ? "Permuta integral — nada a cobrar."
          : !hasPaymentPlan
            ? "Defina o plano de cobrança e gere os links de pagamento."
            : paidCount > 0
              ? `${paidCount} de ${installments.length} parcelas pagas.`
              : "Envie o link de pagamento ao cliente.",
      cta: { label: hasPaymentPlan ? "Cobrar" : "Gerar cobrança", action: "go-payments" },
    },
    {
      key: "delivered",
      label: "Entregue",
      state: delivered ? "done" : "todo",
      hint: "Conclua o projeto e marque a entrega para encerrar o lead.",
      cta: { label: "Marcar como entregue", action: "mark-delivered" },
    },
  ];

  const currentIndex = steps.findIndex((s) => s.state !== "done");
  if (currentIndex >= 0) steps[currentIndex].state = "current";

  return { steps, currentIndex, current: currentIndex >= 0 ? steps[currentIndex] : null, lost };
}
